<template>
  <button
    v-if="content.enable_stl && content.stl_products?.length"
    class="content-card-stl"
    type="button"
    aria-label="Shop the look - view products in a drawer"
    @click="openStl"
  >
    <span
      ref="stl"
      class="content-card-stl__button text-other-button-regular button button--fill button--dark"
      :class="{
        [$class('minimised')]: collapseStl
      }"
    >
      <span
        class="content-card-stl__text"
        v-text="$string('collection.shop_the_look')"
      />
      <icon-cart />
    </span>

    <teleport to="body">
      <overlay
        block-class="content-card-stl-overlay"
        direction="left"
        :namespace="namespace"
        type="drawer"
        :prevent-scroll-on-focus="true"
      >
        <template #title>
          <h2
            class="text-body-l-medium"
            v-text="$string('collection.shop_the_look')"
          />
        </template>

        <template #body>
          <div class="content-card-stl__body">
            <div
              v-if="content.stl_image"
              class="content-card-stl__image"
            >
              <responsive-image
                :image="content.stl_image"
                min-max="300-600"
                sizes="400px"
                :padding="false"
              />
            </div>

            <ul class="content-card-stl__products">
              <li
                v-for="product in content.stl_products"
                :key="product.id"
                class="content-card-stl__product-container"
              >
                <a
                  class="content-card-stl__product wishlist-target"
                  :href="product.url"
                >
                  <responsive-image
                    class="content-card-stl__product-image"
                    :image="product.featuredImage"
                    min-max="125-350"
                    sizes="175px"
                  />

                  <div class="mt-l mt-xl-desktop">
                    <h3
                      class="text-body-s-regular"
                      v-text="product.title"
                    />
                    <product-prices
                      class="mt-xs"
                      :available="product.available"
                      :compare-at-price="product.compareAtPrice?.amount"
                      :price="product.price.amount"
                    />
                  </div>
                </a>
              </li>
            </ul>
          </div>
        </template>
      </overlay>
    </teleport>
  </button>
</template>

<script>

/**
 * Vue: Content card shop the look
 * -----------------------------------------------------------------------------
 *
 * @param {Object} content - Content card Liquid object.
 */
import { mapActions } from 'vuex'
import { generateUUID } from '~/helpers/general'

import ResponsiveImage from '~global/images/responsive-image'
import Overlay from '~global/overlay/overlay'
import ProductPrices from '~global/product-prices/product-prices'

import IconCart from '~icons/general/cart.svg'

export default {
  name: 'ContentCardStl',

  components: {
    IconCart,
    Overlay,
    ProductPrices,
    ResponsiveImage,
  },

  props: {
    content: {
      type: Object,
      default: () => ({}),
      required: true,
    },
  },

  data() {
    return {
      collapseStl: false,
    }
  },

  computed: {
    namespace() {
      return `contentCardShopTheLook-${this.content.id}`
    },
  },

  mounted() {
    this.animateButton()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
    }),

    /**
     * Open shop the look
     */
    openStl() {
      this.openOverlay({
        ignoreDismissed: true,
        namespace: this.namespace,
      })
    },

    /**
     * Collapse shop the look button when the entire clickable area is in view
     */
    animateButton() {
      if (!this.$refs.stl) {
        return
      }
      const observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          observer.disconnect()
          setTimeout(() => {
            this.collapseStl = true
          }, 1000)
        }
      }, {
        threshold: 1,
        rootMargin: '-215px 0px -50px',
      })
      observer.observe(this.$refs.stl)
    },
  },
}
</script>

<style lang="scss">
@import './content-card-stl';
</style>
